// src/components/tutorials/JS_Condition.js
import React from "react";
import "../../../AllComponentCSS.css";

export default function Condition() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">⚙️ JavaScript Conditional Statements</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        Conditional statements in JavaScript are used to make decisions in your
        code. They allow your program to execute certain blocks of code only
        when specific conditions are true. This helps your program respond
        dynamically to different situations and user inputs.
      </p>

      {/* 🔹 Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Conditional statements control the flow of a program based on
            certain conditions.
          </li>
          <li>
            JavaScript provides several types of conditional statements like{" "}
            <code className="highlight_code">if</code>,{" "}
            <code className="highlight_code">if...else</code>,{" "}
            <code className="highlight_code">if...else if...else</code>, and{" "}
            <code className="highlight_code">switch</code>.
          </li>
          <li>
            Conditions are usually expressions that evaluate to{" "}
            <code className="highlight_code">true</code> or{" "}
            <code className="highlight_code">false</code>.
          </li>
          <li>
            Logical operators (<code className="highlight_code">&&</code>,{" "}
            <code className="highlight_code">||</code>,{" "}
            <code className="highlight_code">!</code>) are often used to combine
            multiple conditions.
          </li>
        </ul>
      </div>

      {/* 🔸 How It Works */}
      <div className="tutorial-section">
        <h2 className="section-title">🔸 How Conditional Statements Work</h2>
        <p>
          JavaScript checks the condition you provide. If it evaluates to{" "}
          <code className="highlight_code">true</code>, the related block of
          code runs. If it’s <code className="highlight_code">false</code>, the
          program skips it or executes an alternate block.
        </p>
        <p>
          This decision-making ability makes JavaScript interactive and
          responsive, as different paths can be executed based on user actions
          or data values.
        </p>
      </div>

      {/* 🔸 Types of Conditional Statements */}
      <div className="tutorial-section">
        <h2 className="section-title">🔸 Types of Conditional Statements</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>if statement</b> – Executes a block only if the condition is
            true.
          </li>
          <li>
            <b>if...else statement</b> – Provides an alternate block if the
            condition is false.
          </li>
          <li>
            <b>if...else if...else statement</b> – Checks multiple conditions in
            sequence.
          </li>
          <li>
            <b>switch statement</b> – Compares one value to many possible
            matches for cleaner multi-condition logic.
          </li>
        </ul>
      </div>

      {/* 🔸 Real-World Uses */}
      <div className="tutorial-section">
        <h2 className="section-title">🔸 Real-World Use Cases</h2>
        <ul className="tutorial-list-points">
          <li>Validating form inputs before submission.</li>
          <li>Checking login credentials and authentication status.</li>
          <li>Displaying different content based on user roles.</li>
          <li>
            Determining game outcomes or UI changes based on user actions.
          </li>
        </ul>
      </div>

      {/* 🧠 Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Keep conditions simple and readable; avoid deeply nested
            <code className="highlight_code">if</code> blocks.
          </li>
          <li>
            Always use comparison operators correctly (
            <code className="highlight_code">==</code> vs{" "}
            <code className="highlight_code">===</code>).
          </li>
          <li>
            Prefer <code className="highlight_code">switch</code> for multiple
            fixed options instead of many <code>if...else</code>.
          </li>
          <li>
            Test all possible paths to ensure your logic handles every scenario.
          </li>
        </ul>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fde68a, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about each type of conditional statement in detail:
            <ul>
              <li>
                <code className="highlight_code">if</code> statement
              </li>
              <li>
                <code className="highlight_code">if...else</code> statement
              </li>
              <li>
                <code className="highlight_code">if...else if...else</code>{" "}
                statement
              </li>
              <li>
                <code className="highlight_code">switch</code> statement
              </li>
            </ul>
          </li>
          <li>
            Experiment with conditions using user inputs or random values.
          </li>
          <li>
            Try combining conditions with logical operators for more advanced
            logic.
          </li>
          <li>
            Move on to loops next (<code>for</code>, <code>while</code>) for
            repeating tasks.
          </li>
        </ul>
      </div>
    </div>
  );
}
